import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCliente } from '../../hooks/useCliente';
import './LoginClienteModal.css';

interface LoginClienteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToCadastro?: () => void;
}

export const LoginClienteModal: React.FC<LoginClienteModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  onSwitchToCadastro
}) => {
  const { loginCliente, loading, error } = useCliente();
  const [formData, setFormData] = useState({
    email: '',
    senha: ''
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
      // Reset form when closing
      setFormData({
        email: '',
        senha: ''
      });
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar mensagem de erro quando o usuário começar a digitar
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error message
    setErrorMessage('');
    setSubmitStatus('idle');

    // Validação de campos obrigatórios
    if (!formData.email || !formData.senha) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, preencha todos os campos.');
      return;
    }

    // Validação de email
    if (!validateEmail(formData.email)) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, insira um e-mail válido.');
      return;
    }

    // Validação de senha (mínimo 6 caracteres)
    if (formData.senha.length < 6) {
      setSubmitStatus('error');
      setErrorMessage('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    setSubmitStatus('idle');

    try {
      await loginCliente({
        email: formData.email.trim(),
        senha: formData.senha
      });

      setSubmitStatus('success');
      
      // Limpar formulário após sucesso
      setFormData({
        email: '',
        senha: ''
      });

      // Fechar modal após 1 segundo
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1000);
    } catch (err: any) {
      setSubmitStatus('error');
      let errorMsg = err?.message || 'Erro ao fazer login. Por favor, tente novamente.';
      
      // Mensagens mais amigáveis para diferentes tipos de erro
      if (errorMsg.includes('não encontrada') || errorMsg.includes('404')) {
        errorMsg = 'Esta funcionalidade ainda não está disponível. O backend precisa implementar a rota de login de clientes.';
      } else if (errorMsg.includes('inválid') || errorMsg.includes('401') || errorMsg.includes('credenciais')) {
        errorMsg = 'Email ou senha inválidos. Verifique suas credenciais e tente novamente.';
      } else if (errorMsg.includes('não encontrado') || errorMsg.includes('não existe')) {
        errorMsg = 'Cliente não encontrado. Verifique se o email está correto.';
      }
      
      setErrorMessage(errorMsg);
      console.error('Erro ao fazer login:', err);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="login-cliente-modal-overlay" onClick={handleOverlayClick}>
      <div className="login-cliente-modal" onClick={(e) => e.stopPropagation()}>
        <div className="login-cliente-modal-header">
          <h2>Login</h2>
          <button 
            className="login-cliente-modal-close"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>

        <div className="login-cliente-modal-body">
          <p className="login-cliente-modal-description">
            Faça login para acessar seus favoritos e continuar navegando.
          </p>

          <form onSubmit={handleSubmit} className="login-cliente-form">
            <div className="login-cliente-form-group">
              <label htmlFor="email">E-mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                required
                disabled={loading}
                className="login-cliente-form-input"
                autoComplete="email"
              />
            </div>

            <div className="login-cliente-form-group">
              <label htmlFor="senha">Senha *</label>
              <input
                type="password"
                id="senha"
                name="senha"
                value={formData.senha}
                onChange={handleInputChange}
                placeholder="Sua senha"
                required
                disabled={loading}
                className="login-cliente-form-input"
                autoComplete="current-password"
                minLength={6}
              />
            </div>

            {submitStatus === 'success' && (
              <div className="login-cliente-form-message login-cliente-form-message-success">
                Login realizado com sucesso!
              </div>
            )}

            {submitStatus === 'error' && errorMessage && (
              <div className="login-cliente-form-message login-cliente-form-message-error">
                {errorMessage}
              </div>
            )}

            {error && !errorMessage && (
              <div className="login-cliente-form-message login-cliente-form-message-error">
                {error}
              </div>
            )}

            <div className="login-cliente-form-actions">
              <button
                type="button"
                onClick={onClose}
                className="login-cliente-btn login-cliente-btn-cancel"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="login-cliente-btn login-cliente-btn-submit"
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>

            <div className="login-cliente-form-footer">
              <p>
                Não tem uma conta?{' '}
                <button
                  type="button"
                  onClick={onSwitchToCadastro}
                  className="login-cliente-link"
                  disabled={loading}
                >
                  Cadastre-se agora
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

